"use client";

import Link, { type LinkProps } from "next/link";
import type { AnchorHTMLAttributes, MouseEvent, ReactNode } from "react";

import { useInterstitialNavigation } from "@/components/interstitial/InterstitialProvider";

type Props = LinkProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps | "href"> & {
    children: ReactNode;
    interstitial?: boolean;
  };

function hrefToString(href: LinkProps["href"]) {
  if (typeof href === "string") return href;
  if (typeof href === "object" && href && "pathname" in href && typeof href.pathname === "string") {
    const query = href.query
      ? `?${new URLSearchParams(
          Object.entries(href.query).flatMap(([key, value]) => {
            if (value === undefined) return [];
            if (Array.isArray(value)) return value.map((item) => [key, String(item)]);
            return [[key, String(value)]];
          })
        ).toString()}`
      : "";
    const hash = href.hash ? `#${href.hash}` : "";
    return `${href.pathname}${query}${hash}`;
  }
  return null;
}

function isModifiedClick(event: MouseEvent<HTMLAnchorElement>) {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
}

export default function AdAwareLink({
  interstitial = true,
  onClick,
  href,
  children,
  ...props
}: Props) {
  const { requestInterstitialNavigation } = useInterstitialNavigation();

  return (
    <Link
      href={href}
      onClick={(event) => {
        onClick?.(event);
        if (!interstitial || event.defaultPrevented || isModifiedClick(event)) return;
        if (props.target && props.target !== "_self") return;
        if (props.download) return;

        const hrefString = hrefToString(href);
        if (!hrefString) return;

        const handled = requestInterstitialNavigation(hrefString);
        if (!handled) return;

        event.preventDefault();
      }}
      {...props}
    >
      {children}
    </Link>
  );
}
